/**
 * Settings → Scout: words for the API's Scout settings (api#1079).
 *
 * NOTHING HERE DECIDES WHO SEES WHAT. The API combines the tenant's mode, the
 * platform's suggest switch and each rule's proven lock into an audience per
 * rule (fieldiq-api src/lib/scout/mode.ts); this module only names them so the
 * admin reads one sentence per choice.
 */
import type { ScoutLabel, ScoutMode, ScoutNudgeStatus, ScoutRule } from '../types/scout';

export const SCOUT_MODES: ScoutMode[] = ['off', 'shadow', 'suggest'];

export const SCOUT_MODE_COPY: Record<ScoutMode, { label: string; sentence: string }> = {
  off: {
    label: 'Off',
    sentence: "Scout doesn't run. No brief, no nudges, and the assistant can't log a meeting or brief a client.",
  },
  shadow: {
    label: 'Shadow',
    sentence:
      'Scout runs and is measured, but only admins and office staff see its output. Sales reps see nothing yet.',
  },
  suggest: {
    label: 'Suggest',
    sentence:
      'Sales reps see Scout for every check FieldIQ has proven. Checks still learning stay with admins and office staff.',
  },
};

/** Every rule, as the office reads it. Exhaustive over ScoutRule. */
export const SCOUT_RULE_LABEL: Record<ScoutRule, string> = {
  follow_up_due: 'Follow-up due today',
  follow_up_overdue: 'Follow-up overdue',
  customer_quiet: 'Customer gone quiet',
  customer_brief: 'Pre-visit brief',
  meeting_log: 'Meeting log',
};

/** A rule's label, or `—` for a rule this build does not know. Never the raw key. */
export function scoutRuleLabel(rule: string | null | undefined): string {
  return rule && Object.prototype.hasOwnProperty.call(SCOUT_RULE_LABEL, rule)
    ? SCOUT_RULE_LABEL[rule as ScoutRule]
    : '—';
}


/**
 * A nudge's state, as a reviewer reads it. Exhaustive over ScoutNudgeStatus.
 * `shadow` was computed and shown to nobody; `superseded` stopped being true
 * before anyone acted (the follow-up moved, the customer got a visit).
 */
export const SCOUT_STATUS_LABEL: Record<ScoutNudgeStatus, string> = {
  shadow: 'Shadow',
  open: 'Shown to rep',
  acted: 'Acted on',
  dismissed: 'Dismissed',
  superseded: 'No longer true',
};

export function scoutStatusLabel(status: string | null | undefined): string {
  return status && Object.prototype.hasOwnProperty.call(SCOUT_STATUS_LABEL, status)
    ? SCOUT_STATUS_LABEL[status as ScoutNudgeStatus]
    : '—';
}

/** The three verdicts, in the order a reviewer reaches for them. */
export const SCOUT_LABELS: ScoutLabel[] = ['right', 'wrong', 'dont_care'];
export const SCOUT_LABEL_TEXT: Record<ScoutLabel, string> = {
  right: 'Right',
  wrong: 'Wrong',
  dont_care: "Don't care",
};

/**
 * A rate the API computed (0–1), as a percentage. `null` — no denominator, or
 * fewer labels than the API's small-N floor — is `—`, never 0%. The rate is
 * the API's; this only rounds it for display.
 */
export function ratePct(rate: number | null | undefined): string {
  return typeof rate === 'number' && Number.isFinite(rate) ? `${Math.round(rate * 100)}%` : '—';
}

/** A count exactly as sent, or `—`. */
export function count(n: unknown): string {
  return typeof n === 'number' && Number.isFinite(n) ? String(n) : '—';
}
